import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Drawer,
  Button,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ArticleIcon from "@mui/icons-material/Article";
import SportsBarIcon from "@mui/icons-material/SportsBar";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTheme } from "../../utils/theme";
import LanguageSwitcher from "./LanguageSwitcher";
import Navbar from "./Navbar"; // make sure this path is correct

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [themeMode, setThemeMode] = useState("light");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("themeMode") || "light";
    setThemeMode(savedTheme);
  }, []);

  const toggleTheme = () => {
    const newMode = themeMode === "light" ? "dark" : "light";
    setThemeMode(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  const currentLang = i18n.language || "en";

  return (
    <ThemeProvider theme={getTheme(themeMode)}>
      <CssBaseline />

      {/* Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2
      }}>
        {/* Brand */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#00CCDD", fontFamily: "cursive" }}>
            Secret
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "text.secondary", fontFamily: "cursive" }}>
            Message
          </Typography>
        </Box>

        {/* Right controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={toggleTheme}>
            {themeMode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
          {/* Hamburger only on mobile */}
          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Navbar only on non-mobile */}
      {!isMobile && <Navbar />}

      {/* Drawer for mobile */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 250, p: 2 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            py: 1,
            px: 1
          }}>
            <Button
              variant="contained"
              component={Link}
              to={`/${currentLang}`}
              startIcon={<VpnKeyIcon />}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                // color: theme.palette.text.primary,
                // '&:hover': {
                //   backgroundColor: theme.palette.action.hover,
                // }
              }}
              onClick={() => setDrawerOpen(false)}
            >
              {t('navbar.secret')}
            </Button>
            <Divider />
            <Button
              variant="contained"
              component={Link}
              to={`/${currentLang}/blogs`}
              startIcon={<ArticleIcon />}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                // color: theme.palette.text.primary,
                // '&:hover': {
                //   backgroundColor: theme.palette.action.hover,
                // }
              }}
              onClick={() => setDrawerOpen(false)}
            >
              {t('navbar.blogs')}
            </Button>
            <Divider />
            <Button
              variant="contained"
              component="a"
              href="https://truthordarefun.com/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<SportsBarIcon />}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                // color: theme.palette.text.primary,
                // '&:hover': {
                //   backgroundColor: theme.palette.action.hover,
                // }
              }}
              onClick={() => setDrawerOpen(false)}
            >
              {t('navbar.truthOrDare')}
            </Button>
          </Box>

          <Box sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            borderRadius: '28px',
            px: 0.5,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            transition: 'background-color 0.3s'
          }}>
            <LanguageSwitcher />
          </Box>
        </Box>
      </Drawer>

      {/* Page content */}
      {children}
    </ThemeProvider>
  );
};

export default Layout;
